/**
 * ButtonTabs Component
 *
 * A segmented control component that displays a group of mutually exclusive
 * button options. Only one option can be selected at a time.
 *
 * Accessibility: Uses role="group" with aria-pressed buttons rather than
 * role="tablist" because this is a segmented control, not a tabs component.
 * True tabs require tabpanel elements which this component doesn't have.
 * See: https://www.w3.org/WAI/ARIA/apg/patterns/button/
 *
 * @example
 * ```tsx
 * <ButtonTabs
 *   options={[
 *     { id: 'week', label: 'Week' },
 *     { id: 'month', label: 'Month' },
 *   ]}
 *   selected="week"
 *   onSelect={(id) => setView(id)}
 * />
 * ```
 */

import React, { useCallback, useId } from "react";
import classNames from "classnames";
import type { ButtonTabsProps, TabOption } from "./types";
import styles from "./ButtonTabs.module.css";

export const ButtonTabs = React.forwardRef<HTMLDivElement, ButtonTabsProps>(
  (
    {
      options,
      selected,
      onSelect,
      align = "left",
      size = "md",
      fullWidth = false,
      className,
      "data-testid": testId,
      "aria-label": ariaLabel = "Tab selection",
    },
    ref,
  ) => {
    const groupId = useId();

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
        const enabledOptions = options.filter((opt) => !opt.disabled);
        const currentEnabledIndex = enabledOptions.findIndex(
          (opt) => opt.id === options[currentIndex].id,
        );

        let nextIndex = -1;

        switch (event.key) {
          case "ArrowLeft":
          case "ArrowUp":
            event.preventDefault();
            nextIndex =
              currentEnabledIndex > 0
                ? currentEnabledIndex - 1
                : enabledOptions.length - 1;
            break;
          case "ArrowRight":
          case "ArrowDown":
            event.preventDefault();
            nextIndex =
              currentEnabledIndex < enabledOptions.length - 1
                ? currentEnabledIndex + 1
                : 0;
            break;
          case "Home":
            event.preventDefault();
            nextIndex = 0;
            break;
          case "End":
            event.preventDefault();
            nextIndex = enabledOptions.length - 1;
            break;
          default:
            return;
        }

        if (nextIndex >= 0) {
          const nextOption = enabledOptions[nextIndex];
          onSelect(nextOption.id);
          // Focus the new button
          const nextButton = document.querySelector(
            `[data-tab-id="${groupId}-${nextOption.id}"]`,
          ) as HTMLButtonElement;
          nextButton?.focus();
        }
      },
      [options, onSelect, groupId],
    );

    const alignmentStyles = {
      left: styles.alignLeft,
      center: styles.alignCenter,
      right: styles.alignRight,
    };

    const sizeStyles = {
      sm: styles.sizeSm,
      md: styles.sizeMd,
      lg: styles.sizeLg,
    };

    const tabSizeStyles = {
      sm: styles.tabSm,
      md: styles.tabMd,
      lg: styles.tabLg,
    };

    return (
      <div
        ref={ref}
        role="group"
        aria-label={ariaLabel}
        className={classNames(
          styles.container,
          alignmentStyles[align],
          sizeStyles[size],
          { [styles.fullWidth]: fullWidth },
          className,
        )}
        data-testid={testId}
      >
        {options.map((option: TabOption, index: number) => {
          const isSelected = option.id === selected;
          const isDisabled = option.disabled ?? false;

          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={isSelected}
              aria-disabled={isDisabled}
              tabIndex={isDisabled ? -1 : 0}
              data-tab-id={`${groupId}-${option.id}`}
              disabled={isDisabled}
              onClick={() => !isDisabled && onSelect(option.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={classNames(styles.tab, tabSizeStyles[size], {
                [styles.tabFullWidth]: fullWidth,
                [styles.selected]: isSelected,
                [styles.unselected]: !isSelected && !isDisabled,
                [styles.disabled]: isDisabled,
              })}
              data-testid={testId ? `${testId}-tab-${option.id}` : undefined}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  },
);

ButtonTabs.displayName = "ButtonTabs";
